import { Box, Button as MuiButton, Card, Chip, Dialog, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import {
  AutocompleteInput,
  Datagrid,
  DeleteButton,
  EditButton,
  FilterForm,
  FunctionField,
  List,
  Pagination,
  PasswordInput,
  required,
  SelectInput,
  TextField,
  TextInput,
  TopToolbar,
  useListContext,
  useNotify,
  useRefresh,
  useTranslate,
  useUnselectAll,
  Confirm,
} from 'react-admin';
import { Iconify } from 'src/components/iconify';
import { EmptyContent } from 'src/components/empty-content';
import { SplashScreen } from 'src/components/loading-screen';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { CgCreate, CgCreateProps, CgEdit, CgEditProps } from '../../../components/common';
import CustomTagInput from '../../../components/CustomTagInput';
import {
  deleteProxiesBatch,
  listProxyProviders,
  PROXY_PROTOCOL_CHOICES,
  PROXY_TIER_CHOICES,
} from '../../../services/proxyManagerService';
import { Resources } from '../../resource';
import { resetProxyManagerCursorStore } from '../dataProvider';
import ImportCsvDialog from './ImportCsvDialog';
import AuditLogDialog from 'src/pages/admin/components/AuditLog/AuditLogDialog';

const PERMISSION_RESOURCE = 'proxy-manager';
const RESOURCE = Resources.proxyManagerProxies;

const useProviderChoices = () => {
  const [choices, setChoices] = useState<{ id: string; name: string }[]>([]);

  useEffect(() => {
    let mounted = true;
    listProxyProviders()
      .then((providers) => {
        if (!mounted) return;
        setChoices(providers.map((p) => ({ id: p.name, name: `${p.name} (${p.proxyCount})` })));
      })
      .catch(() => {
        if (mounted) setChoices([]);
      });
    return () => {
      mounted = false;
    };
  }, []);

  return [choices, setChoices] as const;
};

interface ProxyFormProps {
  isCreate: boolean;
}

const ProxyForm = ({ isCreate }: ProxyFormProps) => {
  const [providerChoices, setProviderChoices] = useProviderChoices();
  const translate = useTranslate();

  return (
    <Box sx={{ pt: 2, pb: 1 }}>
      <Stack spacing={1}>
        <SelectInput
          source="protocol"
          label="module.proxyManager.protocol"
          choices={[...PROXY_PROTOCOL_CHOICES]}
          defaultValue="HTTP"
          validate={[required()]}
        />
        <TextInput source="host" label="module.proxyManager.host" validate={[required()]} />
        <TextInput
          source="port"
          label="module.proxyManager.port"
          type="number"
          validate={[required()]}
          format={(v) => (v === undefined || v === null ? '' : v)}
          parse={(v) => (v === '' || v === null ? null : Number(v))}
        />
        <TextInput source="username" label="module.proxyManager.username" />
        <PasswordInput
          source="password"
          label="module.proxyManager.password"
          helperText={!isCreate ? 'module.proxyManager.passwordEditHint' : false}
        />
        <SelectInput
          source="tier"
          label="module.proxyManager.tier"
          choices={[...PROXY_TIER_CHOICES]}
          defaultValue="RESIDENTIAL"
          validate={[required()]}
        />
        <AutocompleteInput
          source="source"
          label="module.proxyManager.source"
          choices={providerChoices}
          onCreate={(filter?: string) => {
            const value = (filter ?? '').trim();
            if (!value) return undefined;
            const existing = providerChoices.find((c) => c.id === value);
            if (existing) return existing;
            const newOption = { id: value, name: value };
            setProviderChoices((prev) => [...prev, newOption]);
            return newOption;
          }}
        />
        <CustomTagInput
          source="tags"
          label={translate('module.proxyManager.tags')}
          defaultValue={[]}
        />
      </Stack>
    </Box>
  );
};

const PROXY_EDITABLE_FIELDS = [
  'protocol',
  'host',
  'port',
  'username',
  'password',
  'tier',
  'source',
  'tags',
];

const transformProxy = (data: any) => {
  // Only send editable fields; never pass id or read-only fields (geo, isp, hasPassword...) in the body.
  const clean: Record<string, any> = {};
  PROXY_EDITABLE_FIELDS.forEach((key) => {
    if (data[key] !== undefined) {
      clean[key] = data[key];
    }
  });
  if (!clean.password) delete clean.password;
  if (clean.username === '') clean.username = null;
  if (clean.source === '') clean.source = null;
  return clean;
};

const EditPopup = (props: CgEditProps) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const t = useTranslate();

  const onSuccess = () => {
    notify(t('module.proxyManager.proxyUpdateSuccess'), {
      type: 'success',
      autoHideDuration: 1500,
    });
    props.onClose?.();
    refresh();
  };

  const onError = (error: any) => {
    notify(error?.message || t('module.proxyManager.unknownError'), { type: 'error' });
  };

  return (
    <CgEdit
      {...props}
      title="module.proxyManager.editProxy"
      resource={RESOURCE}
      mutationMode="pessimistic"
      transform={transformProxy}
      mutationOptions={{ onSuccess, onError }}
    >
      <ProxyForm isCreate={false} />
    </CgEdit>
  );
};

const CreatePopup = (props: CgCreateProps) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const t = useTranslate();

  const onSuccess = () => {
    notify(t('module.proxyManager.proxyCreateSuccess'), {
      type: 'success',
      autoHideDuration: 1500,
    });
    props.onClose?.();
    refresh();
  };

  const onError = (error: any) => {
    notify(error?.message || t('module.proxyManager.unknownError'), { type: 'error' });
  };

  return (
    <CgCreate
      {...props}
      title="module.proxyManager.createProxy"
      resource={RESOURCE}
      transform={transformProxy}
      mutationOptions={{ onSuccess, onError }}
    >
      <ProxyForm isCreate />
    </CgCreate>
  );
};

const ListLoadingOverlay = () => {
  const { isLoading } = useListContext();
  if (!isLoading) return null;
  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 10,
        bgcolor: 'rgba(255,255,255,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <SplashScreen />
    </Box>
  );
};

const ProxyListActions = ({
  onCreate,
  onImport,
  onAudit,
}: {
  onCreate: () => void;
  onImport: () => void;
  onAudit: () => void;
}) => {
  const { filterValues, setFilters, setPage } = useListContext();
  const translate = useTranslate();
  const [providerChoices] = useProviderChoices();
  const hasActiveFilters = Object.keys(filterValues ?? {}).length > 0;

  const ACTIVE_CHOICES = useMemo(
    () => [
      { id: 'true', name: translate('common.active', { _: 'Active' }) },
      { id: 'false', name: translate('common.inactive', { _: 'Inactive' }) },
    ],
    [translate]
  );

  const handleResetFilters = () => {
    resetProxyManagerCursorStore(RESOURCE);
    setPage(1);
    setFilters({}, []);
  };

  return (
    <TopToolbar sx={{ alignItems: 'center', p: 2, width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          gap: 2,
          flexWrap: 'wrap',
          '& form': { display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', flex: 1 },
          '& .ra-input': { mt: 0, mb: 0 },
          '& .MuiFormControl-root': { mt: 0, mb: 0 },
          '& .MuiInputBase-root': { minHeight: 40 },
        }}
      >
        <FilterForm
          filters={[
            <SelectInput
              key="tier"
              source="tier"
              label="module.proxyManager.tier"
              choices={[...PROXY_TIER_CHOICES]}
              alwaysOn
              resettable
              emptyText={translate('actions.all')}
              size="small"
              sx={{ minWidth: 140 }}
            />,
            <SelectInput
              key="protocol"
              source="protocol"
              label="module.proxyManager.protocol"
              choices={[...PROXY_PROTOCOL_CHOICES]}
              alwaysOn
              resettable
              emptyText={translate('actions.all')}
              size="small"
              sx={{ minWidth: 120 }}
            />,
            <TextInput
              key="host"
              source="host"
              label="module.proxyManager.host"
              alwaysOn
              resettable
              size="small"
              sx={{ minWidth: 160 }}
            />,
            <TextInput
              key="port"
              source="port"
              label="module.proxyManager.port"
              type="number"
              alwaysOn
              resettable
              size="small"
              sx={{ minWidth: 110 }}
            />,
            <TextInput
              key="countryCode"
              source="countryCode"
              label="module.proxyManager.countryCode"
              alwaysOn
              resettable
              size="small"
              sx={{ minWidth: 120 }}
            />,
            <TextInput
              key="ispName"
              source="ispName"
              label="module.proxyManager.ispName"
              alwaysOn
              resettable
              size="small"
              sx={{ minWidth: 160 }}
            />,
            <SelectInput
              key="source"
              source="source"
              label="module.proxyManager.source"
              choices={providerChoices}
              alwaysOn
              resettable
              emptyText={translate('actions.all')}
              size="small"
              sx={{ minWidth: 160 }}
            />,
            <TextInput
              key="tags"
              source="tags"
              label={translate('module.proxyManager.tags')}
              alwaysOn
              resettable
              size="small"
              sx={{ minWidth: 140 }}
            />,
            <SelectInput
              key="isActive"
              source="isActive"
              label="module.proxyManager.status"
              choices={ACTIVE_CHOICES}
              alwaysOn
              resettable
              emptyText={translate('actions.all')}
              size="small"
              sx={{ minWidth: 120 }}
            />,
          ]}
        />
        <MuiButton
          variant="outlined"
          size="medium"
          onClick={handleResetFilters}
          disabled={!hasActiveFilters}
        >
          {translate('common.reset')}
        </MuiButton>
        <PermissionGuard resource={PERMISSION_RESOURCE} action="create" fallback={<></>}>
          <MuiButton variant="contained" size="medium" onClick={onCreate}>
            {translate('module.proxyManager.createProxy')}
          </MuiButton>
        </PermissionGuard>
        <PermissionGuard resource={PERMISSION_RESOURCE} action="create" fallback={<></>}>
          <MuiButton
            variant="contained"
            size="medium"
            onClick={onImport}
            startIcon={<Iconify icon="eva:upload-fill" />}
          >
            {translate('module.proxyManager.importCsv')}
          </MuiButton>
        </PermissionGuard>
        <PermissionGuard resource={PERMISSION_RESOURCE} action="delete" fallback={<></>}>
          <ProxyBulkActions />
        </PermissionGuard>
      </Box>
    </TopToolbar>
  );
};

const ProxyPagination = () => {
  const { setPage } = useListContext();
  const refresh = useRefresh();
  const translate = useTranslate();

  const handleRefresh = () => {
    resetProxyManagerCursorStore(RESOURCE);
    setPage(1);
    refresh();
  };

  return (
    <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} px={2} py={1}>
      <MuiButton variant="outlined" size="small" onClick={handleRefresh}>
        {translate('common.refresh', { _: 'Refresh' })}
      </MuiButton>
      <Pagination rowsPerPageOptions={[10, 20, 50, 100]} />
    </Box>
  );
};

const ProxyBulkActions = () => {
  const { selectedIds = [] } = useListContext();
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const unselectAll = useUnselectAll(RESOURCE);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await deleteProxiesBatch(selectedIds.map((id) => String(id)));
      notify(
        translate('module.proxyManager.deleteBatchSuccess', { smart_count: selectedIds.length }),
        {
          type: 'success',
          autoHideDuration: 1500,
        }
      );
      unselectAll();
      resetProxyManagerCursorStore(RESOURCE);
      refresh();
    } catch (error: any) {
      notify(error?.message || translate('module.proxyManager.unknownError'), { type: 'error' });
    } finally {
      setLoading(false);
      setOpen(false);
    }
  };

  return (
    <>
      <MuiButton
        variant="contained"
        color="error"
        size="medium"
        disabled={selectedIds.length === 0}
        startIcon={<Iconify icon="solar:trash-bin-trash-bold" />}
        onClick={() => setOpen(true)}
      >
        {translate('module.proxyManager.deleteBatch')}
        {selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}
      </MuiButton>
      <Confirm
        isOpen={open}
        loading={loading}
        title={translate('module.proxyManager.deleteBatch')}
        content={translate('module.proxyManager.deleteBatchConfirm', {
          smart_count: selectedIds.length,
        })}
        onConfirm={handleConfirm}
        onClose={() => setOpen(false)}
      />
    </>
  );
};

const TierChip = ({ tier }: { tier?: string }) => {
  const colorMap: Record<string, 'success' | 'info' | 'warning' | 'secondary' | 'default'> = {
    RESIDENTIAL: 'success',
    DATACENTER: 'info',
    MOBILE: 'warning',
    ISP: 'secondary',
  };
  return <Chip label={tier || '-'} color={colorMap[tier || ''] ?? 'default'} size="small" />;
};

export const ProxiesPanel = () => {
  const translate = useTranslate();
  const [popup, setPopup] = useState<ReactElement>();
  const [auditLogOpen, setAuditLogOpen] = useState(false);

  return (
    <>
      <Card sx={{ mt: 2, pb: 2 }}>
        <List
          resource={RESOURCE}
          actions={
            <ProxyListActions
              onCreate={() => setPopup(<CreatePopup onClose={() => setPopup(undefined)} />)}
              onImport={() => setPopup(<ImportCsvDialog onClose={() => setPopup(undefined)} />)}
              onAudit={() => setAuditLogOpen(true)}
            />
          }
          empty={false}
          perPage={20}
          exporter={false}
          pagination={<ProxyPagination />}
          sort={{ field: 'createdAt', order: 'DESC' }}
        >
          <Box sx={{ overflow: 'auto', position: 'relative' }}>
            <ListLoadingOverlay />
            <Datagrid
              rowClick={false}
              empty={<EmptyContent />}
              bulkActionButtons={<></>}
              sx={{ '& .RaDatagrid-tableWrapper': { overflowX: 'auto' } }}
            >
              <TextField source="protocol" label="module.proxyManager.protocol" sortable={false} />
              <FunctionField
                label="module.proxyManager.address"
                sortable={false}
                render={(record: any) => `${record?.host}:${record?.port}`}
              />
              <TextField source="username" label="module.proxyManager.username" sortable={false} />
              <FunctionField
                label="module.proxyManager.tier"
                sortable={false}
                render={(record: any) => <TierChip tier={record?.tier} />}
              />
              <FunctionField
                label="module.proxyManager.geo"
                sortable={false}
                render={(record: any) => {
                  const geo = record?.geo;
                  if (!geo) return '-';
                  const parts = [geo.countryCode, geo.city, geo.region].filter(Boolean);
                  return parts.length ? parts.join(' / ') : '-';
                }}
              />
              <FunctionField
                label="module.proxyManager.ispName"
                sortable={false}
                render={(record: any) => record?.isp?.name || '-'}
              />
              <TextField source="source" label="module.proxyManager.source" sortable={false} />
              <FunctionField
                label="module.proxyManager.tags"
                sortable={false}
                render={(record: any) => (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {(record?.tags ?? []).length
                      ? record.tags.map((tag: string) => (
                          <Chip key={tag} label={tag} size="small" variant="outlined" />
                        ))
                      : '-'}
                  </Stack>
                )}
              />
              <FunctionField
                label="module.proxyManager.status"
                sortable={false}
                render={(record: any) => (
                  <Chip
                    label={
                      record?.isActive
                        ? translate('common.active', { _: 'Active' })
                        : translate('common.inactive', { _: 'Inactive' })
                    }
                    color={record?.isActive ? 'success' : 'default'}
                    size="small"
                  />
                )}
              />
              <FunctionField
                label="module.proxyManager.createdAt"
                sortable={false}
                render={(record: any) => (
                  <Typography variant="body2" noWrap>
                    {record?.createdAt ? dayjs(record.createdAt).format('YYYY-MM-DD HH:mm') : '-'}
                  </Typography>
                )}
              />
              <FunctionField
                label="common.actions"
                textAlign="right"
                render={(record: any) => (
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <PermissionGuard
                      resource={PERMISSION_RESOURCE}
                      action="update"
                      fallback={<></>}
                    >
                      <EditButton
                        onClick={(e) => {
                          e.preventDefault();
                          setPopup(
                            <EditPopup id={record.id} onClose={() => setPopup(undefined)} />
                          );
                        }}
                      />
                    </PermissionGuard>
                    <PermissionGuard
                      resource={PERMISSION_RESOURCE}
                      action="delete"
                      fallback={<></>}
                    >
                      <DeleteButton
                        resource={RESOURCE}
                        record={record}
                        mutationMode="pessimistic"
                        redirect={false}
                      />
                    </PermissionGuard>
                  </Stack>
                )}
              />
            </Datagrid>
          </Box>
        </List>
      </Card>

      <Dialog open={Boolean(popup)} onClose={() => setPopup(undefined)} maxWidth="sm" fullWidth>
        {popup}
      </Dialog>
    </>
  );
};

export default ProxiesPanel;
